import React, { useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import AuthInput from "../login/AuthInput";
import { usernameRules } from "../../utils/validationRules";
import "../login/Login.css";

export type ForgotPasswordFormInputs = {
  username: string;
};

export type ForgotPasswordRequestPageProps = {
  logoSrc?: string;
  logoAlt?: string;
  logoStyle?: React.CSSProperties;
  renderLogo?: React.ReactNode;
  loading?: boolean;
  onSubmit?: (values: { email: string }) => Promise<{
    ok: boolean;
    message?: string;
  }>;
  titleText?: string;
  buttonLabel?: string;
  successMessageText?: string;
};

const ForgotPasswordRequestPage: React.FC<ForgotPasswordRequestPageProps> = ({
  logoSrc,
  logoAlt,
  logoStyle,
  renderLogo,
  loading,
  onSubmit,
  titleText = "パスワード再発行",
  buttonLabel = "認証コードの発行",
  successMessageText = "メールの送信が完了しました。確認お願いします",
}) => {
  const [_status, setStatus] = useState("");
  const [apiError, setApiError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ForgotPasswordFormInputs>();

  const usernameValue = watch("username");
  const isFormValid = Boolean(usernameValue?.trim());

  const doSubmit: SubmitHandler<ForgotPasswordFormInputs> = async (data) => {
    setApiError("");
    setSuccessMessage("");
    setStatus("");
    try {
      if (onSubmit) {
        const result = await onSubmit({ email: data.username });
        if (!result.ok) {
          setStatus("fail");
          setApiError(result.message || "メールアドレスが見つかりません");
          return;
        }
      }
      setStatus("success");
      setSuccessMessage(successMessageText);
    } catch (_e) {
      setStatus("fail");
      setApiError("メールアドレスが見つかりません");
    }
  };

  return (
    <Box className="container">
      <Box
        component="form"
        onSubmit={handleSubmit(doSubmit)}
        noValidate
        autoComplete="off"
        className="formContainer"
      >
        <Box className="fieldContainer">
          <Box className="logoContainer">
            {renderLogo ? (
              renderLogo
            ) : logoSrc ? (
              <img src={logoSrc} alt={logoAlt || "Logo"} style={logoStyle} />
            ) : null}
          </Box>
          <Box className="check_mail_content">
            <p className="check_mail_title">{titleText}</p>
          </Box>
          <Box className="inputContainer">
            <AuthInput
              label="メールアドレス"
              name="username"
              placeholder="メールアドレスを入力してください"
              register={register}
              error={!!errors.username}
              helperText={
                typeof errors.username?.message === "string"
                  ? errors.username?.message
                  : undefined
              }
              apiError={apiError}
              successMessage={successMessage}
              isRequired={true}
              type="email"
              rules={usernameRules}
            />
          </Box>
          <Button
            type="submit"
            disableElevation
            variant="contained"
            onMouseDown={(e) => e.preventDefault()}
            className={`button ${!isFormValid || loading ? "buttonDisable" : ""}`}
            disabled={!isFormValid || !!loading}
            sx={{ "&:disabled": { backgroundColor: "#BFE6EF !important" } }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              buttonLabel
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPasswordRequestPage;
